import {
  AllergyWarning, Badge, Button, Card, CareDay, ChildAvatar, ErrorState, Input, LoadingState, MedicationDose, Notice,
  Permissions, Row, Select, Stack, Textarea,
  atTimeOfDay, timeOfDay, todayIso, useApi, useI18n, useSession,
  type CatalogItemResponse, type ChildDetailResponse, type MealAmountEaten,
} from '@daghfal/core';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { childName } from '../names';
import { useQueue } from '../offline/QueueProvider';
import type { QueueEntryKind } from '../offline/queue';

interface QuickAction {
  kind: QueueEntryKind;
  icon: string;
  labelKey: string;
  /** Only offered where the class has nappy logging switched on (FR-STF-03). */
  requiresDiaperLog?: boolean;
}

/**
 * The eight quick actions the client specified, plus the nappy action.
 *
 * Order matters: these sit in a grid a thumb reaches, and the ones used most often are first.
 */
const ACTIONS: QuickAction[] = [
  { kind: 'meal', icon: '🍽️', labelKey: 'action.meal' },
  { kind: 'drink', icon: '💧', labelKey: 'action.drink' },
  { kind: 'sleep', icon: '😴', labelKey: 'action.sleep' },
  { kind: 'activity', icon: '🎨', labelKey: 'action.activity' },
  { kind: 'mood', icon: '😊', labelKey: 'action.mood' },
  { kind: 'diaper', icon: '🧷', labelKey: 'action.diaper', requiresDiaperLog: true },
  { kind: 'photo', icon: '📸', labelKey: 'action.photo' },
  { kind: 'note', icon: '📝', labelKey: 'action.note' },
];

const AMOUNTS: MealAmountEaten[] = ['All', 'Most', 'Some', 'Little', 'Refused'];

/**
 * One child's quick-entry screen (FR-STF-03, FR-STF-04), and what was recorded for them today (FR-STF-10).
 *
 * The three-tap rule shapes everything: open the child, tap the action, confirm. The time is now unless the teacher
 * changes it, every option has a sensible default already selected, and the entry goes into the offline queue
 * rather than straight to the network — so a teacher in a corridor with no signal gets exactly the same taps as one
 * standing next to the router (FR-STF-09). Correcting what is already recorded needs the network, and says so.
 */
export function QuickEntry() {
  const { childId } = useParams();
  const api = useApi();
  const { t } = useI18n();
  const { can } = useSession();

  const id = Number(childId);

  const child = useQuery({
    queryKey: ['child', id],
    queryFn: () => api.children.get(id),
    enabled: Number.isFinite(id),
  });

  if (child.isPending) return <LoadingState />;
  if (child.isError) return <ErrorState error={child.error} onRetry={() => void child.refetch()} />;

  const data = child.data;

  return (
    <Stack>
      <Link className="dg-button dg-button--ghost" to="/">{t('child.back')}</Link>

      <Row>
        <ChildAvatar child={data} photoUrl={data.photoUrl} allergies={data.allergies} />
        <span className="dg-stack dg-stack--tight">
          <strong style={{ fontSize: 'var(--dg-text-lg)' }}>{childName(data)}</strong>
          {data.className && <Badge>{data.className}</Badge>}
        </span>
      </Row>

      {/* Above the actions, not below them: the teacher must see the allergen before they reach for the
          meal button, not after (FR-STF-07). */}
      <AllergyWarning allergies={data.allergies} />

      <EntryActions group={[data]} />

      {can(Permissions.health.administerMedication) && <ChildMedication childId={data.id} />}

      {can(Permissions.health.createIncident) && (
        <Link className="dg-button dg-button--secondary" to={`/child/${data.id}/incident`}>🩹 {t('incidents.new')}</Link>
      )}

      <CareDay childId={data.id} />
    </Stack>
  );
}

/** The child's medication in force today, each with a dose recorded against it (FR-CHD-16). */
function ChildMedication({ childId }: { childId: number }) {
  const api = useApi();
  const { t } = useI18n();

  const medications = useQuery({
    queryKey: ['medications', childId],
    queryFn: () => api.health.medications(childId),
  });

  const current = (medications.data ?? []).filter((medication) => medication.isCurrent);
  if (medications.isError) return <ErrorState error={medications.error} onRetry={() => void medications.refetch()} />;
  if (current.length === 0) return null;

  return (
    <Card title={t('medication.title')}>
      <Stack>
        {current.map((medication) => <MedicationDose key={medication.id} medication={medication} />)}
      </Stack>
    </Card>
  );
}

/**
 * The same quick actions for several children at once (FR-STF-05, PLAN-gaps C9): the class board's selection.
 *
 * One entry goes into the queue for all of them, as the API takes it, and the confirmation names every child it was
 * recorded for, so nobody is left out without the teacher seeing it.
 */
export function GroupEntry() {
  const api = useApi();
  const { t, tn } = useI18n();
  const [params] = useSearchParams();

  const ids = (params.get('children') ?? '').split(',').map(Number).filter((id) => Number.isInteger(id) && id > 0);

  const children = useQueries({
    queries: ids.map((id) => ({ queryKey: ['child', id], queryFn: () => api.children.get(id) })),
  });

  const failed = children.find((query) => query.isError);
  if (failed) return <ErrorState error={failed.error} onRetry={() => void failed.refetch()} />;
  if (children.some((query) => query.isPending)) return <LoadingState />;

  const loaded = children.map((query) => query.data!);
  if (loaded.length === 0) {
    return (
      <Stack>
        <Link className="dg-button dg-button--ghost" to="/">{t('child.back')}</Link>
        <Notice tone="info">{t('group.none')}</Notice>
      </Stack>
    );
  }

  return (
    <Stack>
      <Link className="dg-button dg-button--ghost" to="/">{t('child.back')}</Link>

      <Card title={tn('group.title', loaded.length)}>
        <Row className="dg-row--wrap">
          {loaded.map((child) => <Badge key={child.id} tone="neutral">{childName(child)}</Badge>)}
        </Row>
      </Card>

      {loaded.filter((child) => child.allergies.some((allergy) => allergy.requiresAcknowledgement)).map((child) => (
        <Stack key={child.id} tight>
          <strong>{childName(child)}</strong>
          <AllergyWarning allergies={child.allergies} />
        </Stack>
      ))}

      <EntryActions group={loaded} />
    </Stack>
  );
}

/**
 * The action grid and the sheet behind each action, for one child or several, writing into the offline queue.
 */
function EntryActions({ group }: { group: ChildDetailResponse[] }) {
  const api = useApi();
  const queue = useQueue();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [open, setOpen] = useState<QuickAction | null>(null);
  const [recordedFor, setRecordedFor] = useState<string[] | null>(null);

  const single = group.length === 1 ? group[0] : undefined;
  const classIds = [...new Set(group.map((child) => child.classId))];

  // Nappies are offered where every child logs them: their own setting, else their class's (FR-DAY-08).
  const diaperLog = group.every((child) => child.logsDiapers);

  const catalog = useQuery({ queryKey: ['catalog'], queryFn: () => api.catalog.list() });

  const needsAcknowledgement = group.some((child) => child.allergies.some((allergy) => allergy.requiresAcknowledgement));

  const record = (kind: QueueEntryKind, payload: Record<string, unknown>, recordedAt: string) => {
    queue.enqueue({
      kind,
      childIds: group.map((child) => child.id),
      classId: classIds.length === 1 ? classIds[0]! : null,
      // The moment the entry is about, not the moment it is eventually delivered.
      recordedAt,
      payload,
    });
    setOpen(null);
    setRecordedFor(group.map(childName));
  };

  const actions = ACTIONS.filter((action) =>
    (!action.requiresDiaperLog || diaperLog)
    // A photograph is tagged and uploaded on its own screen, one child at a time from here.
    && (action.kind !== 'photo' || single !== undefined));

  const items = (type: CatalogItemResponse['catalogType']) =>
    (catalog.data ?? []).filter((item) => item.catalogType === type && item.isActive);

  return (
    <>
      {recordedFor && (
        <Notice tone="success" title={t('care.saved')}>
          {recordedFor.length > 1 && <p>{t('group.recordedFor', { names: recordedFor.join('، ') })}</p>}
          {t('queue.hint')}
        </Notice>
      )}

      {open === null ? (
        <Card title={t('child.title')}>
          <div className="dg-quick-actions">
            {actions.map((action) => (
              <button
                key={action.kind}
                type="button"
                className="dg-quick-action"
                onClick={() => {
                  setRecordedFor(null);
                  // A photograph carries bytes, which the offline queue cannot hold across a closed tab.
                  // It goes to its own screen and uploads while the file is still in hand.
                  if (action.kind === 'photo' && single) {
                    navigate(`/child/${single.id}/photo`);
                    return;
                  }
                  setOpen(action);
                }}
              >
                <span className="dg-quick-action__icon" aria-hidden="true">{action.icon}</span>
                <span>{t(action.labelKey)}</span>
              </button>
            ))}
          </div>
        </Card>
      ) : (
        <ActionSheet
          action={open}
          items={items}
          needsAcknowledgement={needsAcknowledgement}
          onCancel={() => setOpen(null)}
          onRecord={record}
        />
      )}
    </>
  );
}

/**
 * The detail behind one quick action.
 *
 * Every sheet opens with its defaults already chosen, so "record" is reachable without touching anything
 * — which is what makes the common case three taps rather than seven. The time is now unless changed, one tap
 * away, and never later than now (FR-STF-04).
 */
function ActionSheet({ action, items, needsAcknowledgement, onCancel, onRecord }: {
  action: QuickAction;
  items: (type: CatalogItemResponse['catalogType']) => CatalogItemResponse[];
  needsAcknowledgement: boolean;
  onCancel: () => void;
  onRecord: (kind: QueueEntryKind, payload: Record<string, unknown>, recordedAt: string) => void;
}) {
  const { t, settings } = useI18n();

  const mealTypes = items('MealType');
  const foods = items('Food');
  const intake = items('IntakeLevel');
  const moods = items('Mood');
  const activities = items('Activity');
  const diapers = items('DiaperStatus');

  // The one catalogue this action chooses from, empty for the kinds that choose from none.
  const choices = action.kind === 'drink' ? intake
    : action.kind === 'mood' ? moods
    : action.kind === 'activity' ? activities
    : action.kind === 'diaper' ? diapers
    : [];

  const [mealTypeId, setMealTypeId] = useState<number | ''>(mealTypes[0]?.id ?? '');
  const [amount, setAmount] = useState<MealAmountEaten>('Most');
  const [chosenFoods, setChosenFoods] = useState<number[]>([]);
  const [food, setFood] = useState('');
  const [catalogItemId, setCatalogItemId] = useState<number | ''>(choices[0]?.id ?? '');
  const [note, setNote] = useState('');
  const [cream, setCream] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  // Empty means now. Set only once the teacher chooses to change it.
  const [time, setTime] = useState('');
  const [changingTime, setChangingTime] = useState(false);

  // The sheet can open before the catalogue query has answered, in which case the defaults above were
  // taken from an empty list. Without this the select shows its first option while the state behind it is
  // still empty, and the API refuses the entry for a choice the teacher can see is made.
  const firstMealTypeId = mealTypes[0]?.id;
  const firstChoiceId = choices[0]?.id;

  useEffect(() => {
    if (firstMealTypeId !== undefined) setMealTypeId((current) => (current === '' ? firstMealTypeId : current));
  }, [firstMealTypeId]);

  useEffect(() => {
    if (firstChoiceId !== undefined) setCatalogItemId((current) => (current === '' ? firstChoiceId : current));
  }, [firstChoiceId]);

  const recordedAt = () => (time ? atTimeOfDay(todayIso(settings), time, settings) : new Date().toISOString());
  const inFuture = time !== '' && new Date(atTimeOfDay(todayIso(settings), time, settings)).getTime() > Date.now();

  // A meal for a child with a severe allergy cannot be saved until the warning is acknowledged. The API
  // refuses it too; this only spares the teacher a round trip to be told so (FR-CHD-07).
  const blocked = (action.kind === 'meal' && needsAcknowledgement && !acknowledged)
    // Both are required by the API. Refusing here costs a disabled button; refusing there costs a queued
    // entry the teacher has to find and retype.
    || (action.kind === 'meal' && mealTypeId === '')
    || (requiresChoice(action.kind) && catalogItemId === '')
    || inFuture;

  const options = (list: CatalogItemResponse[]) => list.map((item) => (
    <option key={item.id} value={item.id}>{item.icon ? `${item.icon} ` : ''}{item.nameAr}</option>
  ));

  const submit = () => {
    const common = { note: note.trim() || null };
    const at = recordedAt();

    switch (action.kind) {
      case 'meal': {
        // The catalogue's foods first, by id and by name, then whatever was typed: names are kept with the
        // ids so renaming a food next term never rewrites what a past meal said (FR-DAY-04).
        const fromCatalogue = foods.filter((item) => chosenFoods.includes(item.id))
          .map((item) => ({ catalogItemId: item.id, name: item.nameAr }));
        // Split on the Arabic comma as well as the Latin one: a teacher types what their keyboard has.
        const typed = food.split(/[,،]/).map((name) => ({ catalogItemId: null, name: name.trim() }))
          .filter((item) => item.name);
        const foodItems = [...fromCatalogue, ...typed];
        onRecord('meal', {
          ...common,
          mealTypeCatalogItemId: mealTypeId === '' ? null : mealTypeId,
          amountEaten: amount,
          allergyAcknowledged: acknowledged,
          foodItems: foodItems.length > 0 ? foodItems : null,
        }, at);
        break;
      }
      case 'drink':
      case 'mood':
      case 'activity':
        onRecord(action.kind, { ...common, catalogItemId: catalogItemId === '' ? null : catalogItemId }, at);
        break;
      case 'diaper':
        onRecord('diaper', {
          ...common,
          catalogItemId: catalogItemId === '' ? null : catalogItemId,
          creamApplied: cream,
        }, at);
        break;
      case 'sleep':
        // The nap stays open; it is ended from the child's day or the class board, where it shows as asleep
        // (FR-DAY-05).
        onRecord('sleep', { ...common, startAt: at, endAt: null }, at);
        break;
      case 'note':
        onRecord('note', { body: note.trim() }, at);
        break;
      default:
        onCancel();
    }
  };

  return (
    <Card title={t(action.labelKey)}>
      <Stack>
        <Row>
          {changingTime ? (
            <label className="dg-field">
              <span className="dg-field__label">{t('care.time')}</span>
              <Input
                type="time"
                value={time || timeOfDay(new Date(), settings)}
                onChange={(event) => setTime(event.target.value)}
              />
            </label>
          ) : (
            <>
              <span>{t('entry.timeNow')}</span>
              <Button variant="ghost" onClick={() => { setTime(timeOfDay(new Date(), settings)); setChangingTime(true); }}>
                {t('entry.changeTime')}
              </Button>
            </>
          )}
        </Row>
        {inFuture && <Notice tone="warning">{t('care.time.future')}</Notice>}

        {action.kind === 'meal' && (
          <>
            <Select
              aria-label={t('care.mealType')}
              value={mealTypeId}
              onChange={(event) =>
                setMealTypeId(event.target.value === '' ? '' : Number(event.target.value))}
            >
              {options(mealTypes)}
            </Select>

            {foods.length > 0 && (
              <fieldset className="dg-field">
                <legend className="dg-field__label">{t('care.foodItems')}</legend>
                <Row className="dg-row--wrap">
                  {foods.map((item) => (
                    <label key={item.id} className="dg-checkbox">
                      <input
                        type="checkbox"
                        checked={chosenFoods.includes(item.id)}
                        onChange={(event) => setChosenFoods((current) => (event.target.checked
                          ? [...current, item.id]
                          : current.filter((id) => id !== item.id)))}
                      />
                      <span>{item.icon ? `${item.icon} ` : ''}{item.nameAr}</span>
                    </label>
                  ))}
                </Row>
              </fieldset>
            )}

            <Input
              value={food}
              onChange={(event) => setFood(event.target.value)}
              placeholder={foods.length > 0 ? t('entry.otherFood') : t('care.foodItems')}
              aria-label={foods.length > 0 ? t('entry.otherFood') : t('care.foodItems')}
            />

            <Select
              aria-label={t('care.amountEaten')}
              value={amount}
              onChange={(event) => setAmount(event.target.value as MealAmountEaten)}
            >
              {AMOUNTS.map((value) => (
                <option key={value} value={value}>{t(`care.amount.${value}`)}</option>
              ))}
            </Select>

            {needsAcknowledgement && (
              <label className="dg-checkbox">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                />
                <span>{t('care.allergyAcknowledge')}</span>
              </label>
            )}
          </>
        )}

        {(action.kind === 'drink' || action.kind === 'mood' || action.kind === 'activity'
          || action.kind === 'diaper') && (
          <Select
            aria-label={t(action.labelKey)}
            value={catalogItemId}
            onChange={(event) =>
              setCatalogItemId(event.target.value === '' ? '' : Number(event.target.value))}
          >
            {/* No empty option: the API requires the choice, so offering "none" would only produce a
                refusal the teacher cannot act on, held in the queue as a failed entry. */}
            {options(choices)}
          </Select>
        )}

        {action.kind === 'diaper' && (
          <label className="dg-checkbox">
            <input type="checkbox" checked={cream} onChange={(e) => setCream(e.target.checked)} />
            <span>{t('day.creamApplied')}</span>
          </label>
        )}

        <Textarea
          rows={action.kind === 'note' ? 4 : 2}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={t('care.note')}
          aria-label={t('care.note')}
        />

        <Row>
          <Button onClick={submit} disabled={blocked || (action.kind === 'note' && !note.trim())}>
            {t('action.save')}
          </Button>
          <Button variant="ghost" onClick={onCancel}>{t('action.cancel')}</Button>
        </Row>
      </Stack>
    </Card>
  );
}

/** The kinds the API will not accept without a catalogue choice. */
function requiresChoice(kind: QueueEntryKind): boolean {
  return kind === 'drink' || kind === 'mood' || kind === 'activity' || kind === 'diaper';
}
